import React from 'react';
import { Edit, Trash2, MapPin } from 'lucide-react';
import { IconButton } from '@atoms';
import type { StoreRecord } from '@types';

export interface StoreTableProps {
  stores: StoreRecord[];
  searchTerm: string;
  onEditStore: (store: StoreRecord) => void;
  onDeleteStore: (store: StoreRecord) => void;
  isDeleteDisabled?: (store: StoreRecord) => boolean;
}

const StoreActionButtons: React.FC<{
  onEdit: () => void;
  onDelete: () => void;
  isDeleteDisabled?: boolean;
}> = ({ onEdit, onDelete, isDeleteDisabled = false }) => {
  return (
    <div className="flex items-center justify-end space-x-2">
      <IconButton
        icon={Edit}
        onClick={onEdit}
        variant="ghost"
        size="sm"
        className="text-primary-600 hover:text-primary-800"
        title="Edit Store"
      />
      <IconButton
        icon={Trash2}
        onClick={onDelete}
        variant="ghost"
        size="sm"
        className="text-error-600 hover:text-error-800"
        title="Delete Store"
        disabled={isDeleteDisabled}
      />
    </div>
  );
};

export const StoreTable: React.FC<StoreTableProps> = ({
  stores,
  searchTerm,
  onEditStore,
  onDeleteStore,
  isDeleteDisabled
}) => {
  // Filter stores based on search term
  const filteredStores = stores.filter(store =>
    store.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Sort stores alphabetically by name
  const sortedStores = [...filteredStores].sort((a, b) => a.name.localeCompare(b.name));

  return (
    <div className="bg-background-primary rounded-lg border border-border-primary overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-background-secondary">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">
                Store Name
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-text-secondary uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border-primary">
            {sortedStores.length === 0 ? (
              <tr>
                <td colSpan={2} className="px-6 py-8 text-center text-text-secondary">
                  {searchTerm ? 'No stores found matching your search.' : 'No stores found.'}
                </td>
              </tr>
            ) : (
              sortedStores.map((store) => (
                <tr key={store.id} className="hover:bg-background-secondary">
                  <td className="px-6 py-4">
                    <div className="flex items-center space-x-3">
                      <div className="flex-shrink-0">
                        <MapPin className="w-5 h-5 text-text-secondary" />
                      </div>
                      <div>
                        <div className="text-sm font-medium text-text-primary">
                          {store.name}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-right">
                    <StoreActionButtons
                      onEdit={() => onEditStore(store)}
                      onDelete={() => onDeleteStore(store)}
                      isDeleteDisabled={isDeleteDisabled?.(store) || false}
                    />
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};
